import * as d3 from "d3";

/** Interface representing a node in the tree visualization
 * @param id - Unique identifier for the node
 * @param observation - Optional observation data containing header info
 * @param children - Optional array of child nodes
 * @param name - Optional display name for the node
 */
export interface TreeNode {
  id: string;
  observation?: {
    header?: {
      id: string;
      name: string;
    };
  };
  children?: TreeNode[];
  name?: string;
  calculatedHeight?: number;
}

/** Tree controls to control the layout of the indented list 
 * @param linkHeight - Vertical spacing between tree levels in pixels
 * @param linkStrength - Strength of the force layout links between nodes
 */
export interface TreeConfig {
  linkHeight: number;
  linkStrength: number;
}

/** 
 * A type that extends the D3 "point" node with additional properties needed for visualization
 * @extends d3.HierarchyPointNode<TreeNode>
 * @param x0 - Previous x coordinate for transitions
 * @param y0 - Previous y coordinate for transitions  
 * @param isRoot - Flag indicating if this is the root node
 * @param _children - Cached children when node is collapsed
 */
export type D3TreeNode = d3.HierarchyPointNode<TreeNode> & {
  x0?: number;
  y0?: number;
  isRoot?: boolean;
  _children?: D3TreeNode[];
};

/**
 * Calculates the height needed for a node based on its text content and styling
 */
export function calculateNodeHeight(
  text: string, 
  maxWidth: number, 
  isChatOpen: boolean, 
  isSelected: boolean = false, 
  hasTable: boolean = false
): number {
  // Create a temporary element with the exact same styling as our nodes
  const temp = document.createElement("div");
  temp.style.position = "absolute";
  temp.style.visibility = "hidden";
  temp.style.fontFamily = "var(--chakra-fonts-body)";
  temp.style.fontSize = isChatOpen ? "0.8rem" : "0.95rem";
  temp.style.lineHeight = "1.5";
  temp.style.wordWrap = "break-word";
  temp.style.wordBreak = "break-word";
  
  // Calculate responsive width based on available space
  // Instead of fixed subtractions, use a percentage of available width
  const nodeWidthPercentage = 0.70; // Use 70% of available width
  const calculatedWidth = Math.max(maxWidth * nodeWidthPercentage, 200); // Min width of 200px
  
  temp.style.width = `${calculatedWidth}px`;
  
  // Create the same HTML structure we use in the actual nodes
  if (hasTable) {
    temp.innerHTML = `
      <div style="display: flex; align-items: center; width: 100%;">
        <div style="flex: 1; padding-right: 8px;">${text}</div>
        <div style="flex-shrink: 0; width: 22px; height: 22px;">1</div>
      </div>
    `;
  } else {
    temp.innerHTML = `<div>${text}</div>`;
  }
  
  // Append to body to get accurate measurements
  document.body.appendChild(temp);
  const h = temp.offsetHeight;
  document.body.removeChild(temp);
  
  // Add padding to ensure enough space
  return h + 12; // Increased padding for better spacing
}

/**
 * Returns the chain of ancestors from a node up to the root.
 */
export function getPathToRoot(node: D3TreeNode): D3TreeNode[] {
  const path: D3TreeNode[] = [];
  let current: D3TreeNode | null = node;
  while (current) {
    path.push(current);
    current = current.parent as D3TreeNode | null;
  }
  return path;
}

/**
 * The "connector" path from a child node to its parent
 * (just a simple L-shaped link).
 */
export function connector(d: any) {
  return `M${d.parent.y},${d.parent.x}V${d.x}H${d.y}`;
}

/**
 * Applies collapsed states to all nodes in the tree based on collapsedNodes map
 */
export function applyCollapsedStates(rootNode: D3TreeNode, collapsedNodes: Record<string, boolean>) {
  rootNode.each((n: D3TreeNode) => {
    const nodeId = n.data.id;
    // If node should be collapsed
    if (collapsedNodes[nodeId]) {
      // Move children to _children if they exist
      if (n.children) {
        n._children = n.children;
        n.children = undefined;
      }
    } 
    // If node should be expanded
    else {
      // Restore children from _children if they exist
      if (n._children) {
        n.children = n._children;
        n._children = undefined;
      }
    }
  });
}

/**
 * Generates HTML for a node, including text content and table indicator if applicable
 */
export function generateNodeHTML(text: string, tableIndex: number | undefined, isChatOpen: boolean = false, availableWidth: number, isSelected: boolean = false, errorMsg: string = ""): string {
    return `
    
    <div style="
      display: flex; 
      align-items: center;
      min-height: 28px;
      box-sizing: border-box;
      background-color: transparent;
      border-radius: 4px;
      padding: 4px;
      width: ${availableWidth*0.85}px;
      ">
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex: 1;
        line-height: 1.4;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        max-width: calc(100% - 30px);
        background-color: ${isSelected ? "var(--chakra-colors-purple-50)" : "transparent"};
        color: "var(--chakra-colors-gray-900)" : "white"};
        padding: 4px 8px;
        border-radius: 3px;
        display: flex;
        align-items: center;
        ">
        <span class="node-text" style="flex: 1;">${tableIndex !== undefined ? `${text} (${tableIndex + 1})` : text}</span>
        <div style="display: flex; align-items: center; gap: 4px;">
          <button 
            class="edit-node-btn"
            data-action="edit"
            style="
              opacity: ${isSelected ? 1 : 0};
              transition: opacity 0.2s;
              background: none;
              border: none;
              padding: 2px;
              cursor: pointer;
              color: var(--chakra-colors-gray-500);
              display: flex;
              align-items: center;
              justify-content: center;
            "
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </button>
          ${errorMsg ? `
          <div 
            title="${errorMsg}"
            style="
              opacity: ${isSelected ? 1 : 0};
              transition: opacity 0.2s;
              padding: 2px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 16px;
            "
            >
              ⚠️
            </div>
          ` : ""}
        </div>
      </div>
    </div>
  `;
}

/**
 * Finds a node in the tree by its ID
 * @param root - The root node of the tree
 * @param id - The ID of the node to find
 * @returns The node if found, otherwise null
 */
export const findNodeById = (root: TreeNode, id: string): TreeNode | null => {
  if (root.id === id) return root;
  if (root.children) {
    for (const child of root.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Finds a newly added node in the tree
 * @param current - The current node
 * @param prev - The previous node
 * @returns The ID of the newly added node if found, otherwise null
 */
export const findNewNode = (current: TreeNode, prev: TreeNode | null): string | null => {
  if (!prev) return null;

  // If this node didn't exist before, it's new
  if (!findNodeById(prev, current.id)) {
    return current.id;
  }

  // Recursively check children
  if (current.children) {
    for (const child of current.children) {
      const newNodeId = findNewNode(child, prev);
      if (newNodeId) return newNodeId;
    }
  }
  return null;
};

export {};